import * as _ from "lodash";
import { viewerOptions } from "../viewer";

const LINE_WIDTH = 3;
const DASH_SIZE = 50;
const GAP_SIZE = 200;

export enum NodeType {
    Box,
    Sphere
}

/**
 * Utils for adding graphics to extra viewer overlays.
 */
export class GfxUtils {

    private segments = [];

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private overlayName?: string;
    private modelBuilder: Autodesk.Viewing.ModelBuilder;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, overlayName?: string) {
        this.viewer = viewer;
        this.overlayName = overlayName;
    }

    async addModel(): Promise<number> {
        if (!this.modelBuilder) {
            const sceneBuilder: any = await this.viewer.loadExtension("Autodesk.Viewing.SceneBuilder");
            const modelBuilder: Autodesk.Viewing.ModelBuilder =
                await sceneBuilder.addNewModel({ conserveMemory: false, modelNameOverride: this.overlayName });
            this.modelBuilder = modelBuilder;
        }
        return this.modelBuilder.model.id;
    }

    getModel(): Autodesk.Viewing.Model {
        return this.modelBuilder?.model;
    }

    getModelId(): number {
        return this.modelBuilder?.model?.id;
    }

    update() {
        for (let s of this.segments) {
            this.setCylinderScale(s);
        }
    }

    clear() {
        this.segments = [];
        if (this.overlayName != null) {
            (this.viewer.impl as any).clearOverlay(this.overlayName);
        }
    }

    drawEdgeAsCylinder(geom: THREE.Geometry, material: THREE.Material, lineWidth: number, type: number, cylinderGeometry: THREE.Geometry) {

        // The array for all cylinders
        const edge: THREE.Mesh[] = [];
        let cylinder: THREE.Mesh;

        if (type === 1) { // LinePieces
            for (let i = 0; i < geom.vertices.length; i += 2) {
                cylinder = this.cylinderMesh(geom.vertices[i],
                    geom.vertices[i + 1],
                    material,
                    lineWidth,
                    cylinderGeometry);
                this.setCylinderScale(cylinder, geom.vertices[i], geom.vertices[i + 1]);
                edge.push(cylinder);
            }
        } else { // LineStrip
            for (let i = 0; i < geom.vertices.length - 1; i++) {
                cylinder = this.cylinderMesh(geom.vertices[i],
                    geom.vertices[i + 1],
                    material,
                    lineWidth,
                    cylinderGeometry);
                this.setCylinderScale(cylinder, geom.vertices[i], geom.vertices[i + 1]);
                edge.push(cylinder);
            }
        }

        return edge;
    };


    drawDashedLine(p1: THREE.Vector3, p2: THREE.Vector3, dashSize: number, gapSize: number, material: THREE.Material, width: number, overlayName?: string) {

        const geometry = new THREE.Geometry();
        const line = new THREE.Vector3().subVectors(p2, p1);
        const lineLength = line.length() - gapSize;
        const lineDirection = line.normalize();

        let i = 0;
        const pos = p1.clone();
        let currLength = 0;

        while (currLength < lineLength) {
            const isPointVisible = true;// (this.viewer.navigation as any).isPointVisible(pos);

            if (isPointVisible) {
                geometry.vertices[i] = pos.clone();
                i++;
            }

            pos.addVectors(pos, lineDirection.clone().multiplyScalar(dashSize));

            if (isPointVisible) {
                geometry.vertices[i] = pos.clone();
                i++;
            }

            pos.addVectors(pos, lineDirection.clone().multiplyScalar(gapSize));

            currLength += dashSize + gapSize;
        }

        const lineMesh = this.drawEdgeAsCylinder(geometry, material, width, 1, this.getNewCylinderGeometry());
        (this.viewer.impl as any).addMultipleOverlays(overlayName || this.overlayName, lineMesh);

        for (let mesh of lineMesh) {
            this.segments.push(mesh);
        }

        return lineMesh;
    };

    drawLineAsCylinder(geom: THREE.Geometry, material: THREE.Material, lineWidth: number, overlayName?: string): THREE.Mesh {

        const line =
            this.cylinderMesh(geom.vertices[0],
                geom.vertices[1],
                material,
                lineWidth,
                this.getNewCylinderGeometry());
        this.setCylinderScale(line, geom.vertices[0], geom.vertices[1]);
        (this.viewer.impl as any).addOverlay(overlayName || this.overlayName, line);

        this.segments.push(line);

        return line;
    };

    drawLine(p1: THREE.Vector3,
        p2: THREE.Vector3,
        material: THREE.Material,
        width: number,
        overlayName?: string) {
        const geometry = new THREE.Geometry();
        geometry.vertices.push(p1);
        geometry.vertices.push(p2);
        this.drawLineAsCylinder(geometry, material, width, overlayName);
    }

    async drawConnectingNodes(nodes, color: THREE.Color = new THREE.Color(1, 0, 0), type: NodeType = NodeType.Box): Promise<number> {
        const nodeValues = nodes;
        if (!this.modelBuilder) {
            const sceneBuilder: any = await this.viewer.loadExtension("Autodesk.Viewing.SceneBuilder");
            const modelBuilder: Autodesk.Viewing.ModelBuilder =
                await sceneBuilder.addNewModel({ conserveMemory: false, modelNameOverride: this.overlayName });
            this.modelBuilder = modelBuilder;
        }

        for (let guid of _.keys(nodeValues)) {
            this.drawConnectingNode(Number(guid), nodeValues[guid], this.modelBuilder, color, type);
        }

        return this.modelBuilder.model.id;
    }

    drawConnectingNode(dbId: number, node, modelBuilder: Autodesk.Viewing.ModelBuilder, color: THREE.Color, type: NodeType) {
        let geometry;
        switch (type) {
            case NodeType.Sphere:
                geometry = new THREE.BufferGeometry().fromGeometry(
                    new THREE.SphereGeometry(300 * viewerOptions.unitData.modelToViewerScale, 8, 8));
            break;
            default:
                let size = 500 * viewerOptions.unitData.modelToViewerScale;
                geometry = new THREE.BufferGeometry().fromGeometry(new THREE.BoxGeometry(size, size, size));
        }

        const material = new THREE.MeshPhongMaterial({ color: color as any });
        const mesh = new THREE.Mesh(geometry, material);
        // Convert position to viewer units
        let nodeVector = new THREE.Vector3(node["X"], node["Y"], node["Z"]).toViewerUnits();
        mesh.matrix = new THREE.Matrix4().compose(
            nodeVector,
            new THREE.Quaternion(0, 0, 0, 1),
            new THREE.Vector3(1, 1, 1)
        );
        (mesh as any).dbId = dbId;
        modelBuilder.addMesh(mesh);
    }

    /**
     * Create new plane along the line (baseStart - baseEnd)
     * Direction of the plane is defined with the target point 
     * @param dbId object id for created plane
     * @param baseStart
     * @param baseEnd
     * @param targetPoint
     * @param color
     */
    async drawPlaneNode(dbId: number, baseStart: THREE.Vector3, baseEnd: THREE.Vector3,
        targetPoint: THREE.Vector3, color: THREE.Color): Promise<number> {
        if (!this.modelBuilder) {
            const sceneBuilder: any = await this.viewer.loadExtension("Autodesk.Viewing.SceneBuilder");
            const modelBuilder: Autodesk.Viewing.ModelBuilder =
                await sceneBuilder.addNewModel({ conserveMemory: false, modelNameOverride: this.overlayName });
            this.modelBuilder = modelBuilder;
        }

        // line is defined by p0-p1
        let p0 = baseStart.clone();
        let p1 = baseEnd.clone();

        // any random point outside the line will define plane orientation
        let p2 = targetPoint;
        
        // get direction of line p0-p1
        let direction = p1.clone().sub(p0).normalize();

        // project p2 on line p0-p1
        let line0 = new THREE.Line3(p0, p1);
        var proj = new THREE.Vector3();
        line0.closestPointToPoint(p2, true, proj);

        // calculate plane height / widtht
        const height = targetPoint.distanceTo(proj);

        // get plane side direction
        let localUp = p2.clone().sub(proj).normalize();

        // calc plane normal vector (how we want plane to direct)
        let normal = new THREE.Vector3();
        normal.crossVectors(proj.clone().sub(p0), proj.clone().sub(p2));

        //let size = 500 * viewerOptions.unitData.modelToViewerScale;

        const planeL = p0.distanceTo(p1);
        const planeW = height;

        //let geometry = new THREE.BufferGeometry().fromGeometry(new THREE.PlaneGeometry(planeL, planeW));
        let geometry = new THREE.PlaneBufferGeometry(planeL, planeW);

        //const material = new THREE.MeshPhongMaterial({ color: color as any });
        const material = new THREE.MeshBasicMaterial({ color: color, side: THREE.DoubleSide, transparent: true, opacity: 0.5 });
        const mesh = new THREE.Mesh(geometry, material);
        // Convert position to viewer units
        //let nodeVector = new THREE.Vector3(node["X"], node["Y"], node["Z"]).toViewerUnits();

        mesh.position.copy(p0); //put plane by its center on p0
        mesh.up.copy(localUp); //adjust .up for future .lookAt call
        mesh.lookAt(p0.clone().add(normal)); //rotate plane

        mesh.position.add(localUp.clone().multiplyScalar(planeW / 2));
        mesh.position.add(direction.clone().multiplyScalar(planeL / 2));

        mesh.updateMatrixWorld(true);

        mesh.dbId = dbId;
        this.modelBuilder.addMesh(mesh);

        return this.modelBuilder.model.id;
    }

    // Removes model pointed by model builder
    deleteModel() {
        if (this.modelBuilder) {
            this.viewer.unloadModel(this.modelBuilder.model);
            this.modelBuilder = null;
        }
    }

    deleteNode(dbId: number) {
        if (this.modelBuilder) {
            let meshes = this.modelBuilder.fragList.vizmeshes;
            for (let mesh of meshes) {
                if (mesh && mesh.dbId === dbId) {
                    this.modelBuilder.removeMesh(mesh);
                    break;
                }
            }
        }
    }

    changeNodeColor(dbId: number, color: THREE.Color) {
        let meshes = this.modelBuilder.fragList.vizmeshes;
        for (let mesh of meshes) {
            if (mesh && mesh.dbId === dbId) {
                (mesh.material as THREE.MeshPhongMaterial).color = color;
                this.modelBuilder.sceneUpdated(true, false);
                break;
            }
        }
    }

    private getNewCylinderGeometry() {
        return new THREE.CylinderGeometry(0.1, 0.1, 1, 8, 1, false);
    };

    private cylinderMesh(pointX: THREE.Vector3, pointY: THREE.Vector3, material: THREE.Material, lineWidth: number, cylinderGeometry: THREE.Geometry): THREE.Mesh {

        const direction = new THREE.Vector3().subVectors(pointY, pointX);
        const orientation = new THREE.Matrix4();
        orientation.lookAt(pointX, pointY, new THREE.Object3D().up);
        orientation.multiply(new THREE.Matrix4().set(lineWidth,
            0,
            0,
            0,
            0,
            0,
            lineWidth,
            0,
            0,
            -direction.length(),
            0,
            0,
            0,
            0,
            0,
            1));

        const edge = new THREE.Mesh(cylinderGeometry, material);
        edge.applyMatrix(orientation);
        (edge as any).lmv_line_width = lineWidth;
        edge.position.x = (pointY.x + pointX.x) / 2;
        edge.position.y = (pointY.y + pointX.y) / 2;
        edge.position.z = (pointY.z + pointX.z) / 2;

        return edge;
    };

    private setCylinderScale(cylinderMesh, p1?, p2?) {
        let scale: number;

        if (p1 && p2) {
            const point = this.nearestPointInPointToSegment(this.viewer.navigation.getPosition(), p1, p2);
            scale = this.setScale(point);
        } else {
            scale = this.setScale(cylinderMesh.position);
        }

        if (cylinderMesh.hasOwnProperty("lmv_line_width"))
            scale *= cylinderMesh.lmv_line_width;
        cylinderMesh.scale.x = scale;
        cylinderMesh.scale.z = scale;
    };

    private setScale(point: THREE.Vector3) {

        const pixelSize = 5;

        const nav = this.viewer.navigation;
        const camera = nav.getCamera();
        const position = nav.getPosition();

        const p = point.clone();

        const distance = camera.isPerspective ? p.sub(position).length() : nav.getEyeVector().length();

        const fov = (nav as any).getVerticalFov();
        const worldHeight = 2.0 * distance * Math.tan(THREE.Math.degToRad(fov * 0.5));

        const viewport = nav.getScreenViewport();
        const scale = pixelSize * worldHeight / viewport.height;

        return scale;
    };

    private nearestPointInPointToSegment(point, lineStart, lineEnd, forcePerpendicular?) {

        const X0 = new THREE.Vector3();
        const X1 = new THREE.Vector3();
        let nearestPoint;
        let param;

        X0.subVectors(lineStart, point);
        X1.subVectors(lineEnd, lineStart);
        param = X0.dot(X1);
        X0.subVectors(lineEnd, lineStart);
        param = -param / X0.dot(X0);

        if (param < 0) {
            if (forcePerpendicular) {
                nearestPoint = null;
            } else {
                nearestPoint = lineStart;
            }
        }
        else if (param > 1) {
            if (forcePerpendicular) {
                nearestPoint = null;
            } else {
                nearestPoint = lineEnd;
            }
        }
        else {
            X0.subVectors(lineEnd, lineStart);
            X0.multiplyScalar(param);
            nearestPoint = X0.add(lineStart);
        }

        return nearestPoint;
    };

    static createLineMaterial = (color: string, opacity: number = 1) => {
        return new THREE.MeshBasicMaterial({
            color: parseInt(color, 16),
            depthTest: false,
            depthWrite: false,
            side: THREE.DoubleSide
        });
    }

    getModifiedNodeBoxes(fragIds, fragList) {
        let fragbBoxes: THREE.Box3[] = [];
        if (!this.viewer.overlays.hasScene("Graphics")) {
            this.viewer.overlays.addScene("Graphics");
        }

        const fragbBox = new THREE.Box3();

        fragIds.forEach(fragId => {
            fragList.getWorldBounds(fragId, fragbBox);
            this.drawBox2(fragbBox);
            fragbBoxes.push(fragbBox);
        });

        return fragbBoxes;
    }

    addFragmentGeometry(fragIds, fragList) {
        if (!this.viewer.overlays.hasScene("FragmentGraphics")) {
            this.viewer.overlays.addScene("FragmentGraphics");
        }

        let singleGeometry = new THREE.Geometry();
        let singleMesh = new THREE.Mesh();

        console.log(`Fragments: ${fragIds.length}`);

        //fragIds.forEach(fragId => {
        //    //let g = fragList.getGeometry(fragId) as THREE.Geometry;
        //    let m: THREE.Mesh = fragList.getVizmesh(fragId);
        //    m.updateMatrix();
        //    singleGeometry.merge(new THREE.Geometry().fromBufferGeometry(m.geometry as any), m.matrix);
        //    //singleGeometry.mergeMesh(m);
        //});

        let g = fragList.getGeometry(fragList[0]);
        let m: THREE.Mesh = fragList.getVizmesh(fragList[0]);

        const material = new THREE.MeshPhongMaterial();
        const mesh = new THREE.Mesh(g, material);
        let pos = m.position;
        mesh.position.set(pos.x, pos.y, pos.z);
        this.viewer.overlays.addMesh(mesh, "FragmentGraphics");
    }

    drawBox2(box: THREE.Box3) {
        if (!this.viewer.overlays.hasScene("Graphics")) {
            this.viewer.overlays.addScene("Graphics");
        }

        let size = box.getSize();
        let geometry = new THREE.BoxGeometry(size.x, size.y, size.z);

        const material = new THREE.MeshPhongMaterial();
        const mesh = new THREE.Mesh(geometry, material);
        let pos = box.getCenter();
        mesh.position.set(pos.x, pos.y, pos.z);
        this.viewer.overlays.addMesh(mesh, "Graphics");
    }

    getModifiedWorldBoundingBox(fragIds, fragList) {

        const fragbBox = new THREE.Box3();
        const nodebBox = new THREE.Box3();

        fragIds.forEach(fragId => {

            fragList.getWorldBounds(fragId, fragbBox);
            nodebBox.union(fragbBox);
        });

        return nodebBox;
    }

    getOriginalWorldBoundingBox(fragIds, fragList) {

        var fragBoundingBox = new THREE.Box3();
        var nodeBoundingBox = new THREE.Box3();

        var fragmentBoxes = fragList.boxes;

        fragIds.forEach(fragId => {

            const boffset = fragId * 6;

            fragBoundingBox.min.x = fragmentBoxes[boffset];
            fragBoundingBox.min.y = fragmentBoxes[boffset + 1];
            fragBoundingBox.min.z = fragmentBoxes[boffset + 2];
            fragBoundingBox.max.x = fragmentBoxes[boffset + 3];
            fragBoundingBox.max.y = fragmentBoxes[boffset + 4];
            fragBoundingBox.max.z = fragmentBoxes[boffset + 5];

            nodeBoundingBox.union(fragBoundingBox);
        });

        return nodeBoundingBox;
    }

    drawLines(coordsArray, material) {

        for (let i = 0; i < coordsArray.length; i += 2) {

            const start = coordsArray[i];
            const end = coordsArray[i + 1];

            const geometry = new THREE.Geometry();

            geometry.vertices.push(new THREE.Vector3(
                start.x,
                start.y,
                start.z));

            geometry.vertices.push(new THREE.Vector3(
                end.x,
                end.y,
                end.z));

            (geometry as any).computeLineDistances();

            const line = new THREE.Line(geometry, material);

            (this.viewer.impl as any).addOverlay(this.overlayName, line);
        }
    }

    drawBox(min, max) {

        const material = new THREE.LineBasicMaterial({
            color: 0xffff00,
            linewidth: 2
        });

        this.drawLines([
            { x: min.x, y: min.y, z: min.z },
            { x: max.x, y: min.y, z: min.z },
            { x: max.x, y: min.y, z: min.z },
            { x: max.x, y: min.y, z: max.z },
            { x: max.x, y: min.y, z: max.z },
            { x: min.x, y: min.y, z: max.z },
            { x: min.x, y: min.y, z: max.z },
            { x: min.x, y: min.y, z: min.z },
            { x: min.x, y: max.y, z: max.z },
            { x: max.x, y: max.y, z: max.z },
            { x: max.x, y: max.y, z: max.z },
            { x: max.x, y: max.y, z: min.z },
            { x: max.x, y: max.y, z: min.z },
            { x: min.x, y: max.y, z: min.z },
            { x: min.x, y: max.y, z: min.z },
            { x: min.x, y: max.y, z: max.z },
            { x: min.x, y: min.y, z: min.z },
            { x: min.x, y: max.y, z: min.z },
            { x: max.x, y: min.y, z: min.z },
            { x: max.x, y: max.y, z: min.z },
            { x: max.x, y: min.y, z: max.z },
            { x: max.x, y: max.y, z: max.z },
            { x: min.x, y: min.y, z: max.z },
            { x: min.x, y: max.y, z: max.z }
        ],
            material);

        this.viewer.impl.sceneUpdated(true);
    }

    drawSelectedBoundingBox = (event) => {

        const bBox = this.getModifiedWorldBoundingBox(
            event.fragIdsArray,
            this.viewer.model.getFragmentList()
        );
        this.drawBox(bBox.min, bBox.max);
    }

    drawAxis(viewer: Autodesk.Viewing.GuiViewer3D) {
        const bb = viewer.model.getBoundingBox();
        if (bb == null) {
            return;
        }

        const xM = GfxUtils.createLineMaterial("FF0000", 0.5);
        const yM = GfxUtils.createLineMaterial("00FF00", 0.5);
        const zM = GfxUtils.createLineMaterial("0000FF", 0.5);

        this.drawDashedLine(
            new THREE.Vector3(2 * bb.min.x, 0, 0),
            new THREE.Vector3(0, 0, 0),
            DASH_SIZE,
            GAP_SIZE,
            xM,
            LINE_WIDTH);

        this.drawDashedLine(
            new THREE.Vector3(0, 2 * bb.min.y, 0),
            new THREE.Vector3(0, 0, 0),
            DASH_SIZE,
            GAP_SIZE,
            yM,
            LINE_WIDTH);

        this.drawDashedLine(
            new THREE.Vector3(0, 0, 2 * bb.min.z),
            new THREE.Vector3(0, 0, 0),
            DASH_SIZE,
            GAP_SIZE,
            zM,
            LINE_WIDTH);

        this.drawLine(new THREE.Vector3(2 * bb.max.x, 0, 0),
            new THREE.Vector3(0, 0, 0),
            xM,
            LINE_WIDTH);

        this.drawLine(new THREE.Vector3(0, 2 * bb.max.y, 0),
            new THREE.Vector3(0, 0, 0),
            yM,
            LINE_WIDTH);

        this.drawLine(new THREE.Vector3(0, 0, 2 * bb.max.z),
            new THREE.Vector3(0, 0, 0),
            zM,
            LINE_WIDTH);
    }
}


// Stuff for prototyping/demoing custom geometry and shaders.

function addGeometryV1(modelBuilder, dbId) {
    const boxGeometry = new THREE.BufferGeometry().fromGeometry(new THREE.BoxGeometry(1000, 1000, 1000));
    const boxMaterial = new THREE.MeshPhongMaterial({ color: new THREE.Color(1, 0, 0) });
    const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial);
    boxMesh.matrix = new THREE.Matrix4().compose(
        new THREE.Vector3(-10000, -10000, 0),
        new THREE.Quaternion(0, 0, 0, 1),
        new THREE.Vector3(1, 1, 1)
    );
    (boxMesh as any).dbId = dbId;
    modelBuilder.addMesh(boxMesh);
}

function addGeometryV2(modelBuilder, dbId) {
    const sphereGeometry = new THREE.BufferGeometry().fromGeometry(new THREE.SphereGeometry(500, 8, 8));
    const sphereMaterial = new THREE.MeshPhongMaterial({ color: new THREE.Color(0, 1, 0) });
    const sphereTransform = new THREE.Matrix4().compose(
        new THREE.Vector3(0, 0, 0),
        new THREE.Quaternion(0, 0, 0, 1),
        new THREE.Vector3(1, 1, 1)
    );
    const sphereFragId = modelBuilder.addFragment(sphereGeometry, sphereMaterial, sphereTransform);
    modelBuilder.changeFragmentsDbId(sphereFragId, dbId);
}

function addGeometryV3(modelBuilder, dbId) {
    const cylinderGeometry = new THREE.BufferGeometry().fromGeometry(new THREE.CylinderGeometry(500, 500, 10));
    const cylinderMaterial = new THREE.MeshPhongMaterial({ color: new THREE.Color(0, 0, 1) });
    const cylinderTransform = new THREE.Matrix4().compose(
        new THREE.Vector3(+10000, +10000, 0),
        new THREE.Quaternion(0, 0, 0, 1),
        new THREE.Vector3(1, 1, 1)
    );
    modelBuilder.addMaterial('MyCustomMaterial', cylinderMaterial);
    const cylinderGeomId = modelBuilder.addGeometry(cylinderGeometry);
    const cylinderFragId = modelBuilder.addFragment(cylinderGeomId, 'MyCustomMaterial', cylinderTransform);
    modelBuilder.changeFragmentsDbId(cylinderFragId, dbId);
}

function addGeometryV4(modelBuilder, dbId) {
    const VertexShader = `
    void main() {
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;
    const FragmentShader = `
  #ifdef _LMVWEBGL2_
    #if defined(MRT_NORMALS)
      layout(location = 1) out vec4 outNormal;
      #if defined(MRT_ID_BUFFER)
        layout(location = 2) out vec4 outId;
        #if defined(MODEL_COLOR)
          layout(location = 3) out vec4 outModelId;
        #endif
      #endif
    #elif defined(MRT_ID_BUFFER)
      layout(location = 1) out vec4 outId;
      #if defined(MODEL_COLOR)
        layout(location = 2) out vec4 outModelId;
      #endif
    #endif
  #else
    #define gl_FragColor gl_FragData[0]
    #if defined(MRT_NORMALS)
      #define outNormal gl_FragData[1]
      #if defined(MRT_ID_BUFFER)
        #define outId gl_FragData[2]
        #if defined(MODEL_COLOR)
          #define outModelId gl_FragData[3]
        #endif
      #endif
    #elif defined(MRT_ID_BUFFER)
      #define outId gl_FragData[1]
      #if defined(MODEL_COLOR)
        #define outModelId gl_FragData[2]
      #endif
    #endif
  #endif
 
uniform vec2 resolution;

void main(){
  vec2 st = gl_FragCoord.xy/resolution.xy;
  st.x *= resolution.x/resolution.y;
  vec3 color = vec3(0.0);
  float d = 0.0;

  // Remap the space to -1. to 1.
  st = st *2.-1.;

  // Make the distance field
  d = length( abs(st)-.3 );
  // d = length( min(abs(st)-.3,0.) );
  // d = length( max(abs(st)-.3,0.) );

  // Visualize the distance field
  gl_FragColor = vec4(vec3(fract(d*10.0)),1.0);

  // Drawing with the distance field
  // gl_FragColor = vec4(vec3( step(.3,d) ),1.0);
  // gl_FragColor = vec4(vec3( step(.3,d) * step(d,.4)),1.0);
  // gl_FragColor = vec4(vec3( smoothstep(.3,.4,d)* smoothstep(.6,.5,d)) ,1.0);


 
    #ifdef MRT_ID_BUFFER
      outId = vec4(0.0);
    #endif
    #ifdef MODEL_COLOR
      outModelId = vec4(0.0);
    #endif
    #ifdef MRT_NORMALS
      outNormal = vec4(0.0);
    #endif
  }
`;

    const planeGeometry = new THREE.BufferGeometry().fromGeometry(new THREE.PlaneGeometry(25000, 25000));

    const uniforms = {

        resolution: { type: "v2", value: new THREE.Vector2(window.innerWidth, window.innerHeight) }
    };

    const shaderMaterial = new THREE.ShaderMaterial({
        uniforms: uniforms,
        vertexShader: VertexShader,
        fragmentShader: FragmentShader
    });
    shaderMaterial.side = THREE.DoubleSide;
    (shaderMaterial as any).supportsMrtNormals = true;
    const planeTransform = new THREE.Matrix4().compose(
        new THREE.Vector3(0, 0, -5000),
        new THREE.Quaternion(0, 0, 0, 1),
        new THREE.Vector3(1, 1, 1)
    );

    modelBuilder.addMaterial("shaderMaterial", shaderMaterial, true);
    const planeGeomId = modelBuilder.addGeometry(planeGeometry);
    const planeFragId = modelBuilder.addFragment(planeGeomId, "shaderMaterial", planeTransform);
};

(window as any).geom = async function (viewer) {
    const sceneBuilder = await viewer.loadExtension('Autodesk.Viewing.SceneBuilder');
    const modelBuilder =
        await sceneBuilder.addNewModel({ conserveMemory: false, modelNameOverride: 'My Custom Model' });
    addGeometryV1(modelBuilder, 123456);
    addGeometryV2(modelBuilder, 123456);
    addGeometryV3(modelBuilder, 456789);
    addGeometryV4(modelBuilder, 101010);
    viewer.fitToView([123456], modelBuilder.model);
};
